import { computeFitDistance } from './sceneLighting.js'

export const CAMERA_MODE_STANDARD = 'standard'
export const CAMERA_MODE_WALL = 'wall'
export const CAMERA_MODE_WIDE = 'wide'
export const CAMERA_MODE_FREE = 'free'

export const CAMERA_REFERENCE_ART_WIDTH = 50
export const CAMERA_REFERENCE_ART_HEIGHT = 50
export const CAMERA_STANDARD_PADDING = 1.08
export const CAMERA_WALL_PADDING = 1.02

/**
 * Padding when comparing the piece’s required fit distance to each framing preset
 * (matches prior ad-hoc 1.15 in FrameRenderer).
 */
export const AUTO_FRAMING_PIECE_PADDING = 1.15

/**
 * Padding for the fit backstop on presets that carry `ensureFit`. Deliberately
 * tighter than AUTO_FRAMING_PIECE_PADDING: choosing a preset wants comfortable
 * margin, but pulling the camera back past its own preset is a last resort, and
 * every inch beyond "not cropped" is scale the preset was supposed to fix.
 */
export const FRAMING_CROP_GUARD_PADDING = 1.02

/**
 * Framing presets in order from **tightest** to **loosest** camera.
 * Auto mode picks the tightest preset that still fits the piece; if none do, the last (loosest) wins.
 * Add a farther preset here (e.g. room / suite) when you add a matching `CAMERA_MODE_*` and UI.
 */
export const AUTO_FRAMING_PRESETS = [
  {
    mode: CAMERA_MODE_STANDARD,
    refWidth: CAMERA_REFERENCE_ART_WIDTH,
    refHeight: CAMERA_REFERENCE_ART_HEIGHT,
    padding: CAMERA_STANDARD_PADDING,
  },
  {
    mode: CAMERA_MODE_WALL,
    refWidth: 16 * 12,  // 192" fixed — camera distance doesn't change with wall geometry
    refHeight: 8 * 12,  // 96" fixed
    padding: CAMERA_WALL_PADDING,
  },
  {
    mode: CAMERA_MODE_WIDE,
    // Twice wall's distance. Fit distance is linear in the reference size, so
    // doubling both dimensions doubles the distance exactly — no second way of
    // expressing a preset's scale to keep in step with the first.
    refWidth: 32 * 12,
    refHeight: 16 * 12,
    padding: CAMERA_WALL_PADDING,
    // The loosest preset is the last chance to show the piece whole, so it is
    // the one that gives up holding its scale rather than crop. Standard and
    // wall stay exact: their scale is the point of them.
    ensureFit: true,
  },
]

export const FRAMING_PRESET_BY_MODE = new Map(
  AUTO_FRAMING_PRESETS.map((p) => [p.mode, p])
)

export function getCameraModeRank(mode) {
  return AUTO_FRAMING_PRESETS.findIndex((preset) => preset.mode === mode)
}

export function shouldPromoteCameraMode(currentMode, requestedMode) {
  if (currentMode == null) return true

  const currentRank = getCameraModeRank(currentMode)
  const requestedRank = getCameraModeRank(requestedMode)
  return currentRank !== -1 && requestedRank !== -1 && requestedRank > currentRank
}

export function autoFramingPresetDistance(preset, fov, aspect) {
  return computeFitDistance(preset.refWidth, preset.refHeight, fov, aspect, preset.padding)
}

/**
 * The camera's design field of view, and the viewport height it was chosen for.
 * A preset's scale is defined by what it frames at exactly these numbers.
 */
export const FOV_BASE = 65
export const BASELINE_VIEWPORT_HEIGHT = 900

/**
 * The aspect a preset's reference rectangle is defined against. Only used to
 * turn a preset into a scale — it is not imposed on the canvas.
 */
export const PRESET_REFERENCE_ASPECT = 16 / 10

/**
 * Field of view is what absorbs a change in canvas size, so the camera does not
 * have to move to keep the scale.
 *
 * Moving the camera and changing the field of view both change how much fits on
 * screen, but only moving the camera changes perspective: at a fixed position
 * every point projects to the same place relative to every other, and the field
 * of view just decides how much of that projection is kept. So scaling the field
 * of view with the canvas holds pixels-per-inch AND perspective at once, which
 * moving the camera cannot do.
 *
 * Clamped, because the relationship is unbounded: a very tall canvas would want
 * a fisheye and a very short one a telephoto. Inside the clamp both are exactly
 * constant; outside it the field of view sticks and the camera moves to hold the
 * scale instead, which is the older behaviour and only costs perspective.
 */
export const FOV_MIN = 1
export const FOV_MAX = 90

const tanHalf = (deg) => Math.tan((deg * Math.PI) / 360)

export function scaleFovForViewport(viewportH, baselineH = BASELINE_VIEWPORT_HEIGHT) {
  const safeH = Number.isFinite(viewportH) && viewportH > 0 ? viewportH : baselineH
  const ideal = (2 * Math.atan(tanHalf(FOV_BASE) * (safeH / baselineH)) * 180) / Math.PI
  return Math.min(FOV_MAX, Math.max(FOV_MIN, ideal))
}

/**
 * A preset expressed as what it is really for: a fixed number of screen pixels
 * per inch of artwork.
 */
export function presetPixelsPerInch(preset, baselineH = BASELINE_VIEWPORT_HEIGHT) {
  const distance = autoFramingPresetDistance(preset, FOV_BASE, PRESET_REFERENCE_ASPECT)
  return baselineH / (2 * distance * tanHalf(FOV_BASE))
}

/**
 * Camera distance that renders `preset` at its own scale on this canvas.
 *
 * While the field of view tracks the canvas this returns the preset's fixed
 * distance whatever the canvas size — the camera stays put and the field of view
 * does the work. Once the field of view is clamped, the distance takes over so
 * the scale still holds.
 */
export function presetCameraDistance(preset, viewportH, baselineH = BASELINE_VIEWPORT_HEIGHT) {
  const fov = scaleFovForViewport(viewportH, baselineH)
  const pxPerInch = presetPixelsPerInch(preset, baselineH)
  const safeH = Number.isFinite(viewportH) && viewportH > 0 ? viewportH : baselineH
  return safeH / (2 * pxPerInch * tanHalf(fov))
}

/**
 * Auto framing: choose the tightest preset whose scale still leaves the content
 * inside the viewport. Each preset is a fixed pixels-per-inch, so this is a
 * straight measurement in screen pixels rather than a comparison of distances.
 */
export function selectAutoFramingMode({
  contentW,
  contentH,
  viewportW,
  viewportH,
  piecePadding = AUTO_FRAMING_PIECE_PADDING,
  baselineViewportHeight = BASELINE_VIEWPORT_HEIGHT,
}) {
  const safeViewportW = Number.isFinite(viewportW) && viewportW > 0 ? viewportW : 1
  const safeViewportH = Number.isFinite(viewportH) && viewportH > 0 ? viewportH : baselineViewportHeight

  for (const preset of AUTO_FRAMING_PRESETS) {
    const pxPerInch = presetPixelsPerInch(preset, baselineViewportHeight)
    const needW = contentW * pxPerInch * piecePadding
    const needH = contentH * pxPerInch * piecePadding
    if (needW <= safeViewportW && needH <= safeViewportH) return preset.mode
  }
  return AUTO_FRAMING_PRESETS[AUTO_FRAMING_PRESETS.length - 1].mode
}

export function normalizeCameraMode(value) {
  const mode = String(value || '').trim().toLowerCase()
  if (
    mode === CAMERA_MODE_STANDARD
    || mode === CAMERA_MODE_WALL
    || mode === CAMERA_MODE_WIDE
    || mode === CAMERA_MODE_FREE
  ) {
    return mode
  }
  return CAMERA_MODE_STANDARD
}
